/**
 * AshtamshaCalculator - Lazy calculator for D8 Ashtamsha table data
 * Provides async calculation of 8-fold division with chakra integration
 */

import { devLog, devWarn } from '../../../../../utils/devLog';
import { JatakaResponse } from '../../../../../models/api';
import { lazyCalculator } from '../lazy/SimpleLazyCalculator';

/**
 * Ashtamsha result interface
 */
export interface AshtamshaResult {
    planetsByDivision: Record<number, Array<{
        shortName: string;
        isRetrograde: boolean;
        isHora: boolean;
        isChandra: boolean;
        isLagna: boolean;
        displayText: string;
        longitude: number;
        divisionIndex: number;
    }>>;
    calculationTime: number;
    totalPlanets: number;
    divisionsCount: number;
}

/**
 * Calculate D8 Ashtamsha (8-fold division) with progress tracking
 */
async function calculateAshtamsha(
    apiResponse: JatakaResponse
): Promise<AshtamshaResult> {
    const startTime = Date.now();


    // Initialize result structure for 8 divisions
    const planetsByDivision: Record<number, any[]> = {};
    for (let i = 1; i <= 8; i++) {
        planetsByDivision[i] = [];
    }


    // Get current hora planet for highlighting
    const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;
    const horaPlanetName = currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : null;


    // Process each planet for D8 division
    if (apiResponse.graha_positions?.planets) {
        const planets = apiResponse.graha_positions.planets;
        const _totalPlanets = planets.length;

        for (let i = 0; i < planets.length; i++) {
            const planet = planets[i];


            // Parse longitude from position string
            let longitude = 0;
            try {
                const parts = planet.position.split('|');
                if (parts.length >= 3) {
                    longitude = parseFloat(parts[2]);
                }
            } catch (error) {
                devWarn(`⚠️ AshtamshaCalculator: Failed to parse longitude for ${planet.name}`);
                continue;
            }

            // Calculate D8 division (each division = 360°/8 = 45°)
            const divisionIndex = Math.floor((longitude % 360) / 45) + 1;

            // Determine planet properties
            const isLagna = planet.short_name === 'Lg';
            const isChandra = planet.short_name === 'Ch';
            const isRetrograde = planet.is_retrograde || false;
            const isHora = !!(horaPlanetName && (
                planet.short_name === horaPlanetName ||
                planet.name === horaPlanetName
            ));

            // Create display text
            let displayText = planet.short_name;
            if (isRetrograde) {
                displayText = `(${planet.short_name})`;
            }

            const planetDisplay = {
                shortName: planet.short_name,
                isRetrograde,
                isHora,
                isChandra,
                isLagna,
                displayText,
                longitude,
                divisionIndex
            };

            // Add to appropriate division
            planetsByDivision[divisionIndex].push(planetDisplay);

            devLog(`🎯 D8 Ashtamsha Calculator: ${planet.name} (${planet.short_name}) at ${longitude.toFixed(2)}° → Division ${divisionIndex}`);
        }
    }

    // Simulate processing time for realistic async behavior
    await new Promise(resolve => setTimeout(resolve, 30));

    const calculationTime = Date.now() - startTime;
    const totalPlanets = apiResponse.graha_positions?.planets?.length || 0;

    const result: AshtamshaResult = {
        planetsByDivision,
        totalPlanets,
        calculationTime,
        divisionsCount: 8
    };

    devLog(`✅ AshtamshaCalculator: Completed in ${calculationTime}ms - processed ${totalPlanets} planets across ${result.divisionsCount} divisions`);

    return result;
}

/**
 * Generate cache key for D8 Ashtamsha calculation
 */
function generateCacheKey(apiResponse: JatakaResponse): string {
    if (!apiResponse.graha_positions?.planets) {
        return 'no-planets';
    }

    // Create cache key from planet positions and hora
    const planetPositions = apiResponse.graha_positions.planets
        .map(p => `${p.short_name}:${p.position}:${p.is_retrograde ? 'R' : 'D'}`)
        .join('|');

    const horaInfo = apiResponse.detailed_panchanga?.hora_details?.current_planet || 'no-hora';

    return `${planetPositions}|hora:${horaInfo}`;
}

// Register the lazy calculator - simplified for manual cache management
lazyCalculator.register({
    id: 'ashtamsha-d8',
    calculator: calculateAshtamsha,
    cacheKey: generateCacheKey,
    ttl: 900000 // 15 minutes - manual invalidation when positions change
});

export { calculateAshtamsha, generateCacheKey };